import { computed, DOCUMENT, effect, inject } from '@angular/core';
import {
  patchState,
  signalStoreFeature,
  type,
  withComputed,
  withHooks,
  withMethods,
  withProps,
  withState,
} from '@ngrx/signals';
import { LocalStorageKeys } from '@shared-utils';
import { AppEnvironment } from 'environments';

type TokenStoreFeatureState = {
  _token: string | null;
  _isTempToken: boolean;
};

type WithEnvironmentInput = {
  environment: AppEnvironment;
};

export function withToken() {
  return signalStoreFeature(
    {
      props: type<WithEnvironmentInput>(),
    },
    withProps(() => ({
      _windowRefToken: inject(DOCUMENT).defaultView,
    })),
    withState<TokenStoreFeatureState>({
      _token: null,
      _isTempToken: false,
    }),
    withComputed(({ _token, _isTempToken }) => ({
      token: computed(() => _token()),
      isTempToken: computed(() => _isTempToken()),
    })),
    withMethods(store => {
      // Store argument is correctly typed by signalStoreFeature
      const handleStorageChangeEvent = (event: StorageEvent) => {
        if (event.key === LocalStorageKeys.TOKEN) {
          patchState(store, { _token: event.newValue });
          console.log('localStorage "token" changed externally. New value:', event.newValue);
        }
      };

      return {
        setToken: (token: string | null) => {
          patchState(store, { _token: token });
        },
        removeToken: () => {
          patchState(store, { _token: null });
        },
        setIsTempToken: (isTempToken: boolean) => {
          patchState(store, { _isTempToken: isTempToken });
        },
        _handleStorageChangeEvent: handleStorageChangeEvent,
      };
    }),
    withHooks({
      onInit({ _windowRefToken: _windowRef, _token, _handleStorageChangeEvent, _isTempToken, environment, ...store }) {
        if (_windowRef && _windowRef.localStorage.getItem(LocalStorageKeys.TOKEN)) {
          patchState(store, { _token: _windowRef.localStorage.getItem(LocalStorageKeys.TOKEN) });
        }

        const localStorageIsTempToken = _windowRef?.localStorage.getItem(LocalStorageKeys.TEMP_USER_LOGIN_FLAG);
        if (_windowRef && localStorageIsTempToken && ['true', 'false'].includes(localStorageIsTempToken)) {
          patchState(store, {
            _isTempToken: JSON.parse(localStorageIsTempToken),
          });
        }

        // Effect to synchronize token with localStorage
        effect(() => {
          const currentToken = _token();
          if (_windowRef) {
            if (currentToken) {
              _windowRef.localStorage.setItem(LocalStorageKeys.TOKEN, currentToken);
            } else {
              _windowRef.localStorage.removeItem(LocalStorageKeys.TOKEN);
            }
          }
          console.log('localStorage token sync effect run. Current token:', currentToken);
        });

        // Effect to synchronize isTempToken with localStorage
        effect(() => {
          const isTempToken = _isTempToken();
          if (_windowRef) {
            if (isTempToken) {
              _windowRef.localStorage.setItem(LocalStorageKeys.TEMP_USER_LOGIN_FLAG, isTempToken.toString());
            } else {
              _windowRef.localStorage.removeItem(LocalStorageKeys.TEMP_USER_LOGIN_FLAG);
            }
          }
          console.log('isTempToken token sync effect run. Current value:', isTempToken);
        });

        // Add event listener for external localStorage changes (from other tabs/windows only)
        if (_windowRef) {
          _windowRef.addEventListener('storage', _handleStorageChangeEvent);
        }
      },
      onDestroy({ _windowRefToken: _windowRef, _handleStorageChangeEvent }) {
        // Remove event listener on destroy
        if (_windowRef) {
          _windowRef.removeEventListener('storage', _handleStorageChangeEvent);
        }
      },
    }),
  );
}
